import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { BehaviorSubject, distinctUntilChanged, first, skipWhile, Subject, takeUntil, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InfoDetail, InfoHeader } from '../../modules/client/controls/complete-profile-stepper/info-message.enum';
import { InfoPopup } from '../info-popup/info-popup.component';

@Component({
    selector: 'lus-loan-scenario-form',
    templateUrl: './loan-scenario-form.component.html',
    styleUrls: ['./loan-scenario-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoanScenarioFormComponent implements OnInit, OnDestroy {
    @Input() propertyInfoForm: UntypedFormGroup;
    @Input() propertyAddress: string;
    shouldShowValidations = false;
    residenceTypes$ = new BehaviorSubject(null);
    onDestroy$ = new Subject();

    constructor(private http: HttpClient,
        private dialog: MatDialog) { }

    ngOnInit(): void {
        this.initResidences();
        this.initFormAutoCompute();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    initResidences() {
        this.http.get<any>(`${environment.LgvApiBaseUrl}/types/residence`).pipe(first()).subscribe((r) => {
            const sortingArr = ['Primary', 'Secondary', 'Investment'];
            r.sort((a, b) => sortingArr.indexOf(a.name) - sortingArr.indexOf(b.name));
            this.residenceTypes$.next(r);
        });
    }

    initFormAutoCompute() {
        this.propertyInfoForm.get('purchasePrice').valueChanges.pipe(
            skipWhile(() => this.propertyInfoForm.pristine),
            distinctUntilChanged(),
            tap((purchasePrice) => {
                this.resetForm();
                let dpPercent = 0;
                if (this.propertyInfoForm.get('dpPercent').value === null) {
                    this.propertyInfoForm.controls.dpPercent?.setValue(20);
                } else {
                    dpPercent = this.propertyInfoForm.get('dpPercent').value;
                }
                const dp = this.computeDownpayment(purchasePrice, dpPercent);
                const loanAmount = this.computeLoanAmount(purchasePrice, dp);
                this.propertyInfoForm.controls.downPayment?.setValue(dp);
                this.propertyInfoForm.controls.loanAmount?.setValue(loanAmount);
            }), takeUntil(this.onDestroy$)).subscribe();

        this.propertyInfoForm.get('dpPercent').valueChanges.pipe(
            skipWhile(() => this.propertyInfoForm.pristine),
            distinctUntilChanged(),
            tap((dpPercent) => {
                this.resetForm();
                const purchasePrice = this.propertyInfoForm.get('purchasePrice').value;
                const dp = this.computeDownpayment(purchasePrice, dpPercent);
                const loanAmount = this.computeLoanAmount(purchasePrice, dp);
                this.propertyInfoForm.controls.downPayment?.setValue(dp);
                this.propertyInfoForm.controls.loanAmount?.setValue(loanAmount);

            }), takeUntil(this.onDestroy$)).subscribe();

        this.propertyInfoForm.get('downPayment').valueChanges.pipe(
            skipWhile(() => this.propertyInfoForm.pristine),
            distinctUntilChanged(),
            tap((downPayment) => {
                this.resetForm();
                const purchasePrice = this.propertyInfoForm.get('purchasePrice').value;
                const percent = this.computeDpPercent(purchasePrice, downPayment);
                const loanAmount = this.computeLoanAmount(purchasePrice, downPayment);
                if (percent !== this.propertyInfoForm.get('dpPercent').value) {
                    this.propertyInfoForm.controls.dpPercent?.setValue(percent.toFixed(2));
                }
                this.propertyInfoForm.controls.loanAmount?.setValue(loanAmount);

            }), takeUntil(this.onDestroy$)).subscribe();

        this.propertyInfoForm.get('loanAmount').valueChanges.pipe(
            skipWhile(() => this.propertyInfoForm.pristine),
            distinctUntilChanged(),
            tap((loanAmount) => {
                this.resetForm();
                const purchasePrice = this.propertyInfoForm.get('purchasePrice').value;
                const downPayment = (purchasePrice - loanAmount);
                const percent = this.computeDpPercent(purchasePrice, downPayment);
                if (percent !== this.propertyInfoForm.get('dpPercent').value) {
                    this.propertyInfoForm.controls.dpPercent?.setValue(percent.toFixed(2));
                }
                this.propertyInfoForm.controls.downPayment?.setValue(downPayment);
            }), takeUntil(this.onDestroy$)).subscribe();
    }

    computeDownpayment(purchasePrice, dpPercent) {
        return (purchasePrice * (dpPercent / 100));
    }

    computeDpPercent(purchasePrice, downPayment) {
        return ((downPayment / purchasePrice) * 100);
    }

    computeLoanAmount(purchasePrice, downPayment) {
        return (purchasePrice - downPayment);
    }

    resetForm() {
        this.propertyInfoForm.markAsPristine();
        this.shouldShowValidations = false;
    }

    showInfoModal(infoType: string) {
        const dialogRef = this.dialog.open(InfoPopup, {
            width: '500px',
            data: {
                header: InfoHeader[infoType],
                body: InfoDetail[infoType],
                mode: 'generic'
            }
        });
    }

    residenceName(id) {
        const residenceItem = this.residenceTypes$.value?.find(r => r.id === id);
        const suffix = residenceItem?.name === 'Investment' ? ' Property' : ' Home'
        return residenceItem?.name + suffix;
    }

}
